import React from 'react';
import styled from 'styled-components';
import { Contact } from '../../../shared/types/contact';

interface ContactCardProps {
  contact: Contact;
  onAction?: (action: 'message' | 'view-history') => void;
}

const Card = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing[3]};
  padding: ${({ theme }) => theme.spacing[3]};
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  transition: all 0.2s;
  
  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
`;

const Avatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  flex-shrink: 0;
`;

const Content = styled.div`
  flex: 1;
  min-width: 0;
`;

const Organization = styled.div`
  margin: 0;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: 14px;
`;

const Name = styled.p`
  margin: 0 0 ${({ theme }) => theme.spacing[1]} 0;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 13px;
`;

const Role = styled.span`
  display: inline-block;
  padding: ${({ theme }) => `${theme.spacing[1]} ${theme.spacing[2]}`};
  background: ${({ theme }) => theme.colors.gray100};
  color: ${({ theme }) => theme.colors.textSecondary};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-size: 11px;
  font-weight: 500;
  margin-right: ${({ theme }) => theme.spacing[2]};
`;

const VisibilityBadge = styled.span<{ $visibility: string }>`
  display: inline-block;
  padding: ${({ theme }) => `${theme.spacing[1]} ${theme.spacing[2]}`};
  background: ${({ $visibility, theme }) => 
    $visibility === 'Internal' ? theme.colors.gray100 : theme.colors.infoLight + '40'};
  color: ${({ $visibility, theme }) => 
    $visibility === 'Internal' ? theme.colors.textSecondary : theme.colors.infoDark};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-size: 11px;
  font-weight: 500;
`;

const PrimaryBadge = styled.span`
  display: inline-block;
  padding: ${({ theme }) => `${theme.spacing[1]} ${theme.spacing[2]}`};
  background: ${({ theme }) => theme.colors.warningLight + '40'};
  color: ${({ theme }) => theme.colors.warningDark};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-size: 11px;
  font-weight: 600;
  margin-left: ${({ theme }) => theme.spacing[2]};
`;

const RoleCaption = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.textMuted};
  margin-top: ${({ theme }) => theme.spacing[1]};
`;

const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1]};
  margin-top: ${({ theme }) => theme.spacing[2]};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textSecondary};

  a {
    color: ${({ theme }) => theme.colors.primary};
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const Actions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[2]};
  margin-top: ${({ theme }) => theme.spacing[2]};
  font-size: 12px;

  button {
    background: none;
    border: none;
    color: ${({ theme }) => theme.colors.primary};
    cursor: pointer;
    padding: 0;
    font-weight: 500;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

export const ContactCard: React.FC<ContactCardProps> = ({ contact, onAction }) => {
  const initials = contact.name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const visibilityTitle =
    contact.visibility === 'Internal'
      ? 'Internal → Visible to internal users only'
      : 'Customer-visible → Visible to customer portal';

  const roleWhyMap: Record<string, string> = {
    'Ops Owner': 'Execution owner',
    'Compliance Officer': 'Customs & regulatory handling',
    'Carrier': 'Transport operator',
    'Manufacturer': 'Origin contact',
    'Customer': 'Customer point of contact',
    'Customs Broker': 'Broker handling filings',
    'Finance Contact': 'Billing & payments',
  };

  return (
    <Card>
      <Avatar>{initials}</Avatar>
      <Content>
        {contact.organization && <Organization>{contact.organization}</Organization>}
        <div>
          <Name>{contact.name}</Name>
          <div>
            <Role>{contact.role}</Role>
            <VisibilityBadge $visibility={contact.visibility} title={visibilityTitle}>
              {contact.visibility === 'Internal' ? '🔒 Internal' : '👤 Customer-visible'}
            </VisibilityBadge>
            {contact.primary && <PrimaryBadge>⭐ Primary</PrimaryBadge>}
          </div>
          {roleWhyMap[contact.role] && (
            <RoleCaption>{roleWhyMap[contact.role]}</RoleCaption>
          )}
        </div>
        {(contact.email || contact.phone) && (
          <ContactInfo>
            {contact.email && (
              <div>
                📧 <a href={`mailto:${contact.email}`}>{contact.email}</a>
              </div>
            )}
            {contact.phone && <div>📱 {contact.phone}</div>}
            {contact.whatsapp && <div>💬 WhatsApp available</div>}
          </ContactInfo>
        )}
        {onAction && (
          <Actions>
            <button onClick={() => onAction('message')}>Message</button>
            <button onClick={() => onAction('view-history')}>History</button>
          </Actions>
        )}
      </Content>
    </Card>
  );
};
